import { useEffect, useState } from "react";
import { useAircraftNumberSuggestion } from "./useAircraftNumberSuggestion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import MobileFormDialog from "../../../components/ui/mobile-form-dialog";
import { HHMMInput } from "../../../components/ui/hhmm-input";
import { AircraftRegistrationCombobox } from "../../../components/ui/aircraft-combobox";
import { API } from "../../../App";
import { useAircraftGate } from "./AircraftGateDialog";
import { confirmarSemReserva } from "./reservationWarning";

export const FLIGHT_TYPES = [
  { value: "pic", label: "PIC" },
  { value: "dc", label: "DC (Duplo Comando)" },
  { value: "instrucao", label: "Instrução" },
  { value: "viagem", label: "Viagem" },
  { value: "treino", label: "Treino" },
  { value: "outro", label: "Outro" },
  { value: "voo_solo", label: "Voo solo" },
  { value: "solo_supervisionado", label: "Solo supervisionado" },
];

export const PILOT_FLIGHT_TYPE_LABEL = {
  pic: "PIC", dc: "DC", instrucao: "Instrução", viagem: "Viagem", treino: "Treino", outro: "Outro",
  voo_solo: "Voo solo", solo_supervisionado: "Solo supervisionado",
};

export default function PilotFlightForm({ pilotId, flight, onClose, onSaved }) {
  const isEdit = !!flight?.id;
  const { checkGate, gateElement } = useAircraftGate();
  const [form, setForm] = useState({
    flight_date: flight?.flight_date || new Date().toISOString().slice(0, 10),
    pic_minutes: flight?.pic_minutes ?? 60,
    flight_type: flight?.flight_type || "pic",
    aircraft_registration: flight?.aircraft_registration || "",
    navigation_log_number: flight?.navigation_log_number || "",
    service_number: flight?.service_number || "",
    notes: flight?.notes || "",
    ground_run_minutes: flight?.ground_run_minutes ?? 0,
    landings: flight?.landings ?? "",
    supervisor_instructor_id: flight?.supervisor_instructor_id || "",
  });
  const { hasSuggestion, markTouched } = useAircraftNumberSuggestion({ aircraftRegistration: form.aircraft_registration, isEdit, setForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [instructors, setInstructors] = useState([]);
  const isSoloSupervisionado = form.flight_type === "solo_supervisionado";

  // Solo supervisionado: o instrutor supervisor tem de estar sem restrição (mesma regra do formulário do instrutor)
  useEffect(() => {
    if (!isSoloSupervisionado) return;
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/di/instructors`, { withCredentials: true });
        if (!cancel) setInstructors((r.data || []).filter((i) => i.license_status !== "R"));
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [isSoloSupervisionado]);

  const submit = async () => {
    setError("");
    if (!form.flight_date || !form.pic_minutes) { setError("Data e tempo são obrigatórios."); return; }
    if (!(form.navigation_log_number || "").trim()) { setError("Diário de navegação é obrigatório."); return; }
    if (!(form.service_number || "").trim()) { setError("Nº de serviço é obrigatório."); return; }
    if (!(form.aircraft_registration || "").trim()) { setError("A aeronave é obrigatória."); return; }
    if (isSoloSupervisionado && !form.supervisor_instructor_id) { setError("Solo supervisionado requer o instrutor supervisor."); return; }
    // Aeronave com pendências: avisa e pede confirmação, mas nunca impede o registo
    if (!isEdit && !(await checkGate(form.aircraft_registration, form.flight_date))) return;
    if (!isEdit && !(await confirmarSemReserva({ aircraft: form.aircraft_registration, date: form.flight_date, pilotId }))) return;
    setSaving(true);
    try {
      const payload = {
        flight_date: form.flight_date,
        pic_minutes: Number(form.pic_minutes),
        flight_type: form.flight_type,
        aircraft_registration: form.aircraft_registration || null,
        navigation_log_number: form.navigation_log_number.trim(),
        service_number: form.service_number.trim(),
        notes: form.notes || "",
        ground_run_minutes: Number(form.ground_run_minutes || 0),
        landings: form.landings === "" || form.landings == null ? null : Number(form.landings),
        supervisor_instructor_id: isSoloSupervisionado ? form.supervisor_instructor_id : null,
      };
      if (isEdit) {
        await axios.put(`${API}/di/pilots/${pilotId}/flights/${flight.id}`, payload, { withCredentials: true });
        toast.success("Voo atualizado");
      } else {
        await axios.post(`${API}/di/pilots/${pilotId}/flights`, payload, { withCredentials: true });
        toast.success("Voo registado");
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  return (
    <MobileFormDialog open onOpenChange={(o) => { if (!o) onClose(); }} title={isEdit ? "Editar voo" : "Registar voo"}
      description="Voos em aeronaves da escola contam para o total dessa aeronave."
      size="md" onSubmit={submit} submitLabel={isEdit ? "Atualizar" : "Registar"} submitting={saving} testId="di-pilot-flight-form-dialog">
      {gateElement}
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-slate-300">Data *</Label><Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-date" /></div>
        <div>
          <Label className="text-slate-300">Tipo</Label>
          <Select value={form.flight_type} onValueChange={(v) => setForm({ ...form, flight_type: v })}>
            <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">{FLIGHT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-slate-300">Tempo (HH:MM) *</Label><HHMMInput value={form.pic_minutes} onChange={(mins) => setForm({ ...form, pic_minutes: mins ?? 0 })} max={1440} className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-pic" placeholder="1:00" /></div>
      <div><Label className="text-slate-300">Aeronave (matrícula) *</Label><AircraftRegistrationCombobox value={form.aircraft_registration} onChange={(v) => setForm({ ...form, aircraft_registration: v })} className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-ac" /></div>
      {isSoloSupervisionado && (
        <div>
          <Label className="text-slate-300">Instrutor supervisor *</Label>
          <Select value={form.supervisor_instructor_id || ""} onValueChange={(v) => setForm({ ...form, supervisor_instructor_id: v })}>
            <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-supervisor"><SelectValue placeholder="Escolher instrutor supervisor..." /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {instructors.length === 0 && <div className="px-2 py-3 text-xs text-slate-500 italic">Sem instrutores sem restrição disponíveis.</div>}
              {instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} (PU {i.pu_number})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-slate-300">Diário de navegação *</Label><Input value={form.navigation_log_number} onChange={(e) => { markTouched("nav"); setForm({ ...form, navigation_log_number: e.target.value }); }} className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-nav" required /></div>
        <div><Label className="text-slate-300">Nº de serviço *</Label><Input value={form.service_number} onChange={(e) => { markTouched("svc"); setForm({ ...form, service_number: e.target.value }); }} className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-form-svc" required /></div>
        {hasSuggestion && !isEdit && <p className="md:col-span-2 text-[10px] text-slate-400 -mt-2" data-testid="di-pilot-number-hint">Sugerido pela aeronave: diário do último voo e nº de serviço seguinte. Podes alterar.</p>}
      </div>
      <div>
        <Label className="text-slate-300">
          Tempo motor em solo (HH:MM)
          <span className="text-[10px] text-slate-500 font-normal ml-1">— aquecimento, taxi, testes (opcional)</span>
        </Label>
        <HHMMInput value={form.ground_run_minutes ?? 0} onChange={(mins) => setForm({ ...form, ground_run_minutes: mins ?? 0 })} placeholder="0:00" className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-ground-run" />
      </div>
      <div>
        <Label className="text-slate-300">
          Aterragens neste voo
          <span className="text-[10px] text-slate-500 font-normal ml-1">— opcional; sem valor conta 1</span>
        </Label>
        <Input type="number" min={0} max={99} value={form.landings} onChange={(e) => setForm({ ...form, landings: e.target.value })} placeholder="ex.: 4" className="bg-slate-800 border-slate-700" data-testid="di-pilot-flight-landings" />
      </div>
      <div><Label className="text-slate-300">Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" /></div>
      {error && <div className="text-red-400">{error}</div>}
    </MobileFormDialog>
  );
}
